import React, { useState, useEffect } from 'react';
import { format, addMonths, addWeeks, addDays, addYears, parseISO } from 'date-fns';
import { Button } from "react-bootstrap";
import ProgressBar from './ProgressBar';
import ToPeso from './ToPeso';
import "./css/ApplicantProcessedLoans.scss";

// Function to round up to the nearest cent if the second decimal place is not zero
const roundUp = (amount) => {
  const amountString = amount.toFixed(2);
  const secondDecimalPlace = amountString.charAt(amountString.length - 1);

  if (secondDecimalPlace !== '0') {
    return parseFloat((amount + 0.01).toFixed(2));
  }

  return amount;
};

const calculateDueDates = (startDate, interval, term, amount, interest, adv) => {
  const dueDates = [];
  const principalAmount = amount / term;
  const interestAmountInitial = (adv)?(amount * interest) / 100:0;
  const interestAmountMinusAdvance = (((amount * interest) / 100) * term) - interestAmountInitial;
  const interestAmount = interestAmountMinusAdvance / term;
  
  let currentDate = parseISO(startDate);

  for (let i = 0; i < term; i++) {
    switch (interval.toLowerCase()) {
      case 'monthly':
        currentDate = addDays(currentDate, 31);
        break;
      case 'bimonthly': // Twice a month, roughly every two weeks
        currentDate = addWeeks(currentDate, 2);
        break;
      case 'weekly':
        currentDate = addWeeks(currentDate, 1);
        break;
      case 'quarterly':
        currentDate = addMonths(currentDate, 3);
        break;
      case 'semiannual':
        currentDate = addMonths(currentDate, 6);
        break;
      case 'yearly':
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error('Invalid interval');
    }

    dueDates.push({
      date: format(currentDate, 'MM/dd/yyyy'),
      dueAmount: roundUp(principalAmount + interestAmount)
    });
  }

  return dueDates;
};

const ApplicantProcessedLoans = ({ date, interval, term, amount, interest, paymentList, id, loanItem}) => {
  const dueDates = calculateDueDates(date, interval, term, amount, interest, loanItem.advanceInterest);
  const initialIntrest = (loanItem.advanceInterest)?(amount * (interest/100)):0;
  const totalRepayments = dueDates.reduce((acc, due) => acc + due.dueAmount, 0) + initialIntrest;

  const [totalPaid, setTotalPaid] = useState(0);
  const [remainingBalance, setRemainingBalance] = useState(totalRepayments);
  const [totalPenalty, setTotalPenalty] = useState(0);

  useEffect(() => {
    let total = 0;
    let penaltyTotal = 0;
    
    paymentList.forEach(payment => {
        total += (
            parseFloat(payment.paidPrincipal || 0) + 
            parseFloat(payment.paidInterest || 0) + 
            parseFloat(payment.paidOther || 0)
        );
        penaltyTotal += parseFloat(payment.paidPenalty || 0);
    });
    
    setTotalPaid(total + initialIntrest);
    setTotalPenalty(penaltyTotal);
    setRemainingBalance(totalRepayments - totalPaid);

    const findElement = document.querySelectorAll('.pbar');

    findElement.forEach(div => {
      div.classList.add("payment-progress-applicant");
    });

  }, [paymentList, totalRepayments, initialIntrest, totalPaid]);

  return (
    <div className="processed-loans">
      <div className="processed-loans__container">
        <div className="processed-loans__deets">
          <ul>
            <li><i className="fa-regular fa-calendar"></i> {format(parseISO(date), 'MMM dd, yyyy')}</li>
            <li><ToPeso amount={amount} /></li>
            <li><i className="fa-solid fa-arrows-spin"></i> {term}</li>
            {loanItem.advanceInterest &&
              <li><i className="fa-solid fa-plus"></i> <i className="fa-solid fa-percent"></i></li>
            }
            <li><Button variant="primary" href={`/loans/${id}`}><i className="fa-solid fa-circle-info"></i> Details</Button></li>
          </ul>
        </div>
        {loanItem.loanLedger && (
          <div className="loan-ledger">
            <h6><i className="fa-solid fa-book"></i> Loan Ledger</h6>
            <div className="ledger-content">
              {loanItem.loanLedger}
            </div>
          </div>
        )}
        <div className="pbar">
          <ProgressBar 
            totalPayments={parseFloat(totalPaid).toFixed(2)} 
            remainingBalance={parseFloat(remainingBalance).toFixed(2)} 
            totalRepayments={parseFloat(totalRepayments).toFixed(2)}
            totalPenalty={parseFloat(totalPenalty).toFixed(2)}
          />
        </div>
      </div>
    </div>
  );
};


export default ApplicantProcessedLoans;
